import { Injectable, inject, signal } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { NotificationDto } from './notification-api.service';
import { NotificationToastService } from './notification-toast.service';
import { NotificationInboxService } from './notification-inbox.service';
import { PostApiService } from './post-api.service';
import { WorkflowCacheService } from '@/services/workflow-cache.service';
import { PostDto } from '@/helper/dto/cmp/post.dto';
import { LabelDto } from '@/helper/dto/cmp/label.dto';
import { buildSocketUrl } from '@/helper/api-endpoint';

@Injectable({ providedIn: 'root' })
export class NotificationLiveService {
    private readonly authService = inject(AuthService);
    private readonly notificationToastService = inject(NotificationToastService);
    private readonly notificationInbox = inject(NotificationInboxService);
    private readonly postApi = inject(PostApiService);
    private readonly workflowCache = inject(WorkflowCacheService);

    private socket: Socket | null = null;
    private connectedUserId: string | null = null;
    private connectedToken: string | null = null;

    readonly latestNotification = signal<NotificationDto | null>(null);

    connect(userId: string, token: string): void {
        if (!userId || !token) return;
        if (this.socket && this.connectedUserId === userId && this.connectedToken === token) {
            return;
        }

        if (this.socket && this.connectedUserId === userId) {
            this.connectedToken = token;
            this.reconnectWithCurrentToken();
            return;
        }

        this.disconnect();

        this.socket = io(buildSocketUrl('notifications'), {
            auth: (cb) => cb({ token: this.authService.getToken() }),
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000
        });

        this.connectedUserId = userId;
        this.connectedToken = token;

        this.socket.on('notification', (payload: NotificationDto) => {
            this.latestNotification.set(payload);
            this.notificationInbox.onIncoming(payload);
            void this.showToast(payload);
        });

        this.socket.on('connect_error', () => {
            /* keep silent; polling inbox still works */
        });
    }

    disconnect(): void {
        this.socket?.removeAllListeners();
        this.socket?.disconnect();
        this.socket = null;
        this.connectedUserId = null;
        this.connectedToken = null;
    }

    private reconnectWithCurrentToken(): void {
        const socket = this.socket;
        if (!socket) return;
        if (socket.connected) {
            socket.disconnect().connect();
            return;
        }
        socket.connect();
    }

    watchSession(): void {
        this.authService.sessionData.subscribe((session) => {
            if (session.isLoggedIn && session.token && session.user?._id) {
                this.connect(String(session.user._id), session.token);
            } else {
                this.disconnect();
            }
        });
    }

    private async showToast(notification: NotificationDto): Promise<void> {
        let post: PostDto | null = null;
        let labels: LabelDto[] = [];

        if (notification.referenceType === 'post' && notification.referenceId) {
            post = await firstValueFrom(this.postApi.get(notification.referenceId)).catch(() => null);
            if (post?.labelIds?.length) {
                await this.workflowCache.ensureLoaded();
                const allLabels = this.workflowCache.labelsForProject(post.projectId || undefined);
                const labelIds = new Set(post.labelIds.map(String));
                labels = allLabels.filter((label) => label._id && labelIds.has(String(label._id)));
            }
        }

        this.notificationToastService.show({
            notification,
            post,
            labels
        });
    }
}
